/* eslint-disable no-confusing-arrow */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography,
} from '@one-thd/sui-atomic-components';
import { string, func } from 'prop-types';
import {
  params,
  shape,
  arrayOf,
  bool,
  extend,
  arrayOf as arrayType,
  number as numberType,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources';
import {
  Ruler, Refresh, Edit, Duplicate, TrashCan, Search
} from '@one-thd/sui-icons';
import { WallVisualizer } from './WallVisualizer';
import { WallMeasurementInfoSection } from './WallMeasurementInfoSection';
import { CabinetInfoSection } from './CabinetInfoSection';
import { useWallDesigner } from '../hooks/wallDesignerHooks';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../utils/empty-reference';
import { SvgIcon, ZoomSvg } from '../../../Visualizer/components/SvgIcon';
import DesignZoomModal from './DesignZoomModal';
import { CabinetDrawer } from '../Walls/CabinetDrawer';
import { CabinetCollectionDrawer } from '../Walls/CabinetCollectionDrawer';

import { SpacerModal } from './SpacerModal';
import { ConfirmationModal } from '../../CabinetSummary/ConfirmationModal';
import { EditWallModal } from '../Walls/EditWallModal';

import { handleDuplicateCabinet } from './utils/handleDuplicateCabinet';
import { duplicateCabinet } from './utils/duplicateCabinet';
import {
  defaultZoom,
} from './utils/handleAutoZoom';
import { useCabinetVisualizerDigitalDataStore } from '../../../../store';
import { useTrackAddDuplicateAnalytics } from './useTrackAddDuplicateAnalytics';

export const WallDesigner = React.memo(({ designId, handleQuickView }) => {
  // Get the analytics tracker from the zustand store
  const {
    trackChooseCabinetOverlayEvent,
    trackAddItemToWallEvent,
    trackRulerViewInteractionEvent,
  } = useCabinetVisualizerDigitalDataStore();

  // Memoized analytics callback for duplicate cabinet
  const trackAddDuplicateAnalyticsMemo = useTrackAddDuplicateAnalytics(
    trackChooseCabinetOverlayEvent,
    trackAddItemToWallEvent
  );

  const {
    cabinetVisualizer, addCabinetToDesign,
    products, viewportInfo, disableTouchPoints, showDimensions, wallName, wallSize, totalWidth,
    host, metadataBaseUrl, anchorSide, isMobile, isEmptyWall,
    editWallModalProps, handleEditWallModalClose, handleEditWallModalOpen,
    hasValidDesign,
    handleZoomIn, handleZoomOut, handleToggleSegments,
    dndEnabled, segmentsEnabled, segments, Rows, Columns, toggleDrawer, toggleFillerDrawer, toggleSpacerModal,
    handleRenderCallback, setViewportInfo, wallContainerRef, handleWallContainerRefChange, setFloorRect,
    updateDesignProducts, selectedCabinet, isCornerCabinet,
    buttonPosition, restartButtonHeight, restartButtonWidth, setRestartModalOpen,
    restartMessage, restartHeader, handleCloseRestartModal, restartModalOpen,
    handleConfirmRestart, switchButtonPosition,
    iconPosition, iconButtonHeight, iconButtonWidth, openDrawer, openConfirmation,
    handleZoom, handleDimensionsToggle, totalDepth, totalHeight,
    toggleZoomModal, drawerOpen, closeDrawer, getCabinetTouchPoint, handleCabinetDrawerDataReady,
    editMode, editCabinet, isEditCabinetValid, handleCornerCabinetChange,
    collectionDrawerOpen, setCollectionDrawerOpen, handleCollectionSelection,
    spacerModalOpen, getEditSpacerConfig,
    confirmation, setConfirmation, handleClose, removeCabinet

  } = useWallDesigner(designId);

  useEffect(() => {
    if (showDimensions) {
      trackRulerViewInteractionEvent();
    }
  }, [showDimensions]);

  return (
    <>
      <WallMeasurementInfoSection
        wallName={wallName}
        width={wallSize.Columns}
        height={wallSize.Rows}
        handleEditClick={handleEditWallModalOpen}
      />
      <EditWallModal
        open={editWallModalProps.open}
        designId={designId}
        wallName={wallName}
        width={wallSize.Columns}
        height={wallSize.Rows}
        onClose={handleEditWallModalClose}
        handleEditWallSave={handleEditWallModalOpen}
      />
      {!hasValidDesign ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          padding: '20px'
        }}
        >
          <div>Loading wall design...</div>
        </div>
      ) : (
        <div data-component="wall-designer">
          {
            sessionStorage.getItem('cabinetVisualizerDebug') === 'true'
            && (
              <div className="wall-designer-buttons">
                <Button onClick={handleZoomIn}>In</Button>
                <Button onClick={handleZoomOut}>Out</Button>
                <Button
                  style={{ top: 10, width: 100, right: 70 }}
                  onClick={handleToggleSegments}
                >
                  Toggle Segments
                </Button>
              </div>
            )
          }
          <WallVisualizer
            cabinets={products}
            segments={segmentsEnabled ? segments : EMPTY_ARRAY}
            rows={Rows}
            columns={Columns}
            onTouchPoint={toggleDrawer}
            onTouchPointForSpacer={toggleSpacerModal}
            onTouchPointForFiller={toggleFillerDrawer}
            handleQuickView={handleQuickView}
            handleDuplicate={handleDuplicateCabinet}
            disableTouchPoints={disableTouchPoints}
            designId={designId}
            zoom={viewportInfo?.finalZoom || defaultZoom}
            onRender={handleRenderCallback}
            showDimensions={showDimensions}
            totalWidth={totalWidth}
            onViewportChange={setViewportInfo}
            viewportInfo={viewportInfo}
            host={host}
            updateDesignProducts={updateDesignProducts}
            metadataBaseUrl={metadataBaseUrl}
            wallSize={wallSize}
            anchorSide={anchorSide}
            onFloorRectChange={setFloorRect}
            onWallContainerRefChange={handleWallContainerRefChange}
            wallContainerRef={wallContainerRef}
          />
          {!isMobile && !isEmptyWall && !showDimensions && (
            <Button
              variant="secondary"
              style={{
                position: 'absolute',
                bottom: buttonPosition?.bottom || '1rem',
                left: '0.25rem',
                margin: '1rem',
                padding: '0.6rem',
                height: `${restartButtonHeight}px`,
                zIndex: 800,
                width: `${restartButtonWidth}px`
              }}
              startIcon={Refresh}
              onClick={() => setRestartModalOpen(true)}
            >
              Restart Wall
            </Button>
          )}
          {isMobile && !isEmptyWall && !showDimensions && (
            <SvgIcon
              icon={<Refresh />}
              style={{
                position: 'absolute',
                bottom: iconPosition?.bottom || '1rem',
                left: '0.25rem',
                margin: '1rem',
                padding: '0.8rem',
                height: `${iconButtonHeight}px`,
                zIndex: 800,
                width: `${iconButtonWidth}px`,
                color: 'var(--swiper-theme-color)'
              }}
              onClick={() => setRestartModalOpen(true)}
            />
          )}
          {selectedCabinet && !isEmptyWall && (
            <SvgIcon
              icon={<Edit />}
              style={{
                position: 'absolute',
                bottom: iconPosition?.bottom || '1rem',
                right: isCornerCabinet()
                  ? '4.2rem'
                  : '8.4rem',
                margin: '1rem',
                padding: '0.8rem',
                height: `${iconButtonHeight}px`,
                zIndex: 800,
                width: `${iconButtonWidth}px`,
                background: 'rgb(249, 99, 2)', // orange background
                color: '#fff',

              }}
              onClick={(event) => openDrawer(event)}
            />
          )}
          {selectedCabinet && !isEmptyWall && (
            <SvgIcon
              icon={<TrashCan />}
              style={{
                position: 'absolute',
                bottom: iconPosition?.bottom || '1rem',
                margin: '1rem',
                right: '0',
                padding: '0.8rem',
                height: `${iconButtonHeight}px`,
                zIndex: 800,
                width: `${iconButtonWidth}px`,
                background: 'rgb(249, 99, 2)', // orange background
                color: '#fff',
              }}
              onClick={() => setConfirmation(true)}
            />
          )}
          {selectedCabinet && !isEmptyWall && !isCornerCabinet() && (
            <SvgIcon
              icon={<Duplicate />}
              style={{
                position: 'absolute',
                bottom: iconPosition?.bottom || '1rem',
                right: '4.2rem',
                margin: '1rem',
                padding: '0.8rem',
                height: `${iconButtonHeight}px`,
                zIndex: 800,
                width: `${iconButtonWidth}px`,
                background: 'rgb(249, 99, 2)', // orange background
                color: '#fff',
              }}
              onClick={(event) => duplicateCabinet(event, {
                cabinets: products,
                itemId: selectedCabinet.Sku,
                anchorSide,
                addCabinetToDesign,
                designId,
                handleDuplicate: handleDuplicateCabinet,
                host,
                updateDesignProducts,
                metadataBaseUrl,
                itemPrices: cabinetVisualizer.itemPrices,
                trackAddDuplicateAnalytics: trackAddDuplicateAnalyticsMemo,
                id: selectedCabinet.id
              })}
            />
          )}
          {!showDimensions && !isEmptyWall && !selectedCabinet && (
            <SvgIcon
              icon={<Search />}
              style={{
                position: 'absolute',
                bottom: iconPosition?.bottom || '1rem',
                right: '4.4rem',
                margin: '1rem',
                padding: '0.8rem',
                height: `${iconButtonHeight}px`,
                zIndex: 800,
                width: `${iconButtonWidth}px`,
                color: 'var(--swiper-theme-color)'
              }}
              onClick={handleZoom}
            />
          )}
          {!selectedCabinet && !isEmptyWall && (
            <div className={`ruler-icon-btn ${showDimensions ? 'selected' : ''}`}>
              <SvgIcon
                icon={<Ruler />}
                style={{
                  margin: '1rem',
                  position: 'absolute',
                  right: '-0.75rem',
                  padding: '0.8rem',
                  bottom: iconPosition?.bottom || '1rem',
                  height: `${iconButtonHeight}px`,
                  zIndex: 800,
                  width: `${iconButtonWidth}px`,
                  color: 'var(--swiper-theme-color)'
                }}
                onClick={handleDimensionsToggle}
              />
            </div>
          )}
          <DesignZoomModal
            open={toggleZoomModal}
            handleClose={handleZoom}
            width={totalWidth}
            height={totalHeight}
            depth={totalDepth}
            cabinets={products}
            segments={segmentsEnabled ? segments : EMPTY_ARRAY}
            rows={wallSize.Rows}
            columns={wallSize.Columns}
            onRender={handleRenderCallback}
            designId={designId}
          />
          <ConfirmationModal
            isOpen={restartModalOpen}
            onClose={handleCloseRestartModal}
            onConfirm={handleConfirmRestart}
            headerTitle={restartHeader}
            message={restartMessage}
            confirmButtonName="Restart"
            switchButtonPosition={switchButtonPosition}
          />
          <CabinetDrawer
            designId={designId}
            open={drawerOpen}
            onClose={closeDrawer}
            touchpoint={getCabinetTouchPoint()}
            handleQuickView={handleQuickView}
            onDataReady={handleCabinetDrawerDataReady}
            editMode={editMode}
            editCabinet={editCabinet}
            isEditCabinetValid={isEditCabinetValid}
            onCornerCabinetChange={handleCornerCabinetChange}
            cabinets={products}
          />
          <CabinetCollectionDrawer
            designId={designId}
            open={collectionDrawerOpen}
            onClose={() => setCollectionDrawerOpen(false)}
            onSelectCollection={handleCollectionSelection}
          />

          <SpacerModal
            designId={designId}
            openSpacer={spacerModalOpen}
            closeSpacer={closeDrawer}
            touchpoint={getCabinetTouchPoint()}
            editMode={editMode}
            editSpacerConfig={getEditSpacerConfig()}
            editSpacer={editCabinet}
            isEditCabinetValid={isEditCabinetValid}
            cabinets={products}
          />
          <Modal size="sm" open={confirmation} onClose={handleClose}>
            <ModalHeader onClose={handleClose}>Remove Cabinet</ModalHeader>
            <ModalBody>
              <Typography variant="body-base" color="Primary">
                Are you sure you want to remove this cabinet?
              </Typography>
            </ModalBody>
            <ModalFooter position="end">
              <div className="sui-flex sui-gap-3">
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={removeCabinet()}
                >
                  Remove
                </Button>
              </div>
            </ModalFooter>
          </Modal>
        </div>
      )}
      {!showDimensions && hasValidDesign && (
        <CabinetInfoSection width={totalWidth} height={totalHeight} depth={totalDepth} />
      )}
    </>
  );
});

WallDesigner.propTypes = {
  designId: string.isRequired,
  handleQuickView: func.isRequired,
};

WallDesigner.dataModel = extend(
  {
    metadata: params({ parentId: stringType().isRequired() }).shape({
      attributes: arrayOf(shape({
        attributeName: stringType(),
        isSwatch: bool(),
        attributeValues: arrayOf(shape({
          swatchGuid: stringType(),
          value: stringType()
        }))
      })),
      childItemsLookup: arrayOf(shape({
        attributeCombination: stringType(),
        canonicalUrl: stringType(),
        isItemBackOrdered: bool(),
        itemId: stringType()
      })),
      superDuperSku: arrayOf(shape({
        attributes: arrayOf(shape({
          attributeName: stringType(),
          attributeValues: arrayOf(shape({
            selected: bool(),
            superSkuUrl: stringType(),
            value: stringType()
          }))
        }))
      }))
    })
  },
  {
    product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shape({
        brandName: stringType(),
        productLabel: stringType()
      }),
      details: shapeType({
        description: stringType(),
        descriptiveAttributes: arrayType(shapeType({
          name: stringType(),
          value: stringType(),
          bulleted: stringType(),
          sequence: numberType()
        })),
        highlights: arrayType(stringType()),
      }),
      media: shape({
        images: arrayOf(shape({
          url: stringType(),
          sizes: arrayOf(stringType()),
          type: stringType(),
          subType: stringType(),
          altText: stringType()
        })),
      }),
      specificationGroup: arrayType(shape({
        specTitle: stringType(),
        specifications: arrayOf(shape({
          specName: stringType(),
          specValue: stringType()
        }))
      }))
    }),
  }
);
